import React, { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/hooks/use-toast'
import ImageUpload from '@/components/shared/ImageUpload'

interface Client {
  id?: string
  name: string
  industry: string
  logo_url?: string | null
  projects: string[]
  tech_stack: string[]
  website_url?: string | null
  display_order: number
  is_active: boolean
}

interface ClientFormProps {
  client?: Client | null
  onSave: () => void
  onCancel: () => void
}

const ClientForm: React.FC<ClientFormProps> = ({ client, onSave, onCancel }) => {
  const [formData, setFormData] = useState<Omit<Client, 'id'>>({
    name: '',
    industry: '',
    logo_url: null,
    projects: [],
    tech_stack: [],
    website_url: null,
    display_order: 0,
    is_active: true
  })
  const [projectsText, setProjectsText] = useState('')
  const [techStackText, setTechStackText] = useState('')
  const [loading, setLoading] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    if (client) {
      setFormData({
        name: client.name,
        industry: client.industry,
        logo_url: client.logo_url,
        projects: client.projects,
        tech_stack: client.tech_stack,
        website_url: client.website_url,
        display_order: client.display_order,
        is_active: client.is_active
      })
      setProjectsText(client.projects.join('\n'))
      setTechStackText(client.tech_stack.join(', '))
    }
  }, [client])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const projectsArray = projectsText.split('\n').filter(p => p.trim()).map(p => p.trim())
      const techStackArray = techStackText.split(',').filter(t => t.trim()).map(t => t.trim())

      const clientData = {
        ...formData,
        projects: projectsArray,
        tech_stack: techStackArray
      }

      let error
      if (client?.id) {
        // Update existing client
        const { error: updateError } = await supabase
          .from('clients')
          .update(clientData)
          .eq('id', client.id)
        error = updateError
      } else {
        // Create new client
        const { error: insertError } = await supabase
          .from('clients')
          .insert([clientData])
        error = insertError
      }

      if (error) throw error

      toast({
        title: "Success",
        description: `Client ${client?.id ? 'updated' : 'created'} successfully`,
      })

      onSave()
    } catch (error) {
      console.error('Error saving client:', error)
      toast({
        title: "Error",
        description: `Failed to ${client?.id ? 'update' : 'create'} client`,
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleImageUpload = (url: string) => {
    setFormData(prev => ({ ...prev, logo_url: url }))
  }

  const handleImageRemove = () => {
    setFormData(prev => ({ ...prev, logo_url: null }))
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{client?.id ? 'Edit Client' : 'Add New Client'}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="name">Client Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="industry">Industry *</Label>
              <Input
                id="industry"
                value={formData.industry}
                onChange={(e) => setFormData(prev => ({ ...prev, industry: e.target.value }))}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="website_url">Website URL</Label>
            <Input
              id="website_url"
              type="url"
              value={formData.website_url || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, website_url: e.target.value || null }))}
              placeholder="https://example.com"
            />
          </div>

          <div className="space-y-2">
            <ImageUpload
              currentImageUrl={formData.logo_url}
              onImageUpload={handleImageUpload}
              onImageRemove={handleImageRemove}
              label="Client Logo"
              folder="client-logos"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="projects">Projects (one per line)</Label>
            <Textarea
              id="projects"
              value={projectsText}
              onChange={(e) => setProjectsText(e.target.value)}
              placeholder="Enter each project on a new line"
              rows={4}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="tech_stack">Technologies (comma separated)</Label>
            <Textarea
              id="tech_stack"
              value={techStackText}
              onChange={(e) => setTechStackText(e.target.value)}
              placeholder="React, Node.js, PostgreSQL, etc."
              rows={3}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="display_order">Display Order</Label>
              <Input
                id="display_order"
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
              />
              <Label htmlFor="is_active">Active</Label>
            </div>
          </div>

          <div className="flex gap-4">
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : client?.id ? 'Update Client' : 'Create Client'}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

export default ClientForm